import { TIERS } from "constants/pricing";
import { getCurrencySign, shouldShowCurrencyText, CURRENCY_SIGN_RIGHT_OF_NUMBER_LOCALES } from "services/pricing-service";
import { CREDITS_PER_SEAT_PER_TIER, SHARED_CREDITS_POOL_PER_SEAT, getCreditsPerSeatAmount, getSharedCreditsPoolAmount, shouldShowSharedCreditsPoolRow } from "./credits-per-seat-amounts";
export { CREDITS_PER_SEAT_PER_TIER, SHARED_CREDITS_POOL_PER_SEAT, getCreditsPerSeatAmount, getSharedCreditsPoolAmount, shouldShowSharedCreditsPoolRow };
export const MONDAY_LOGO_URL = "https://dapulse-res.cloudinary.com/image/upload/v1777364718/uploads/vpvsufr9s3kxf1jmroil.svg";
// Credits included per tier per month (the "Included" / default amount)
export const CREDITS_PER_TIER = {
    [TIERS.FREE]: 1000,
    [TIERS.BASIC]: 1000,
    [TIERS.STANDARD]: 2000,
    [TIERS.PRO]: 3000,
    [TIERS.ENTERPRISE]: 20000
};
export const TIERS_WITH_CREDITS_DROPDOWN = [];
// Dropdown options per tier: { credits, priceLabel }
// First option is always the included amount (no extra cost)
export const CREDITS_DROPDOWN_OPTIONS = {
    [TIERS.STANDARD]: [
        {
            value: 2000,
            priceLabel: "$20"
        },
        {
            value: 4000,
            priceLabel: "$40"
        },
        {
            value: 8000,
            priceLabel: "$80"
        }
    ],
    [TIERS.PRO]: [
        {
            value: 3000,
            priceLabel: "$30"
        },
        {
            value: 4000,
            priceLabel: "$40"
        },
        {
            value: 8000,
            priceLabel: "$80"
        },
        {
            value: 20000,
            priceLabel: "$200"
        }
    ]
};
// Per-credit prices by period and currency
// INR and ILS are intentionally omitted (not in SUPPORTED_CURRENCIES).
export const CREDIT_PRICE_PER_CREDIT = {
    monthly: {
        usd: 0.0125,
        euro: 0.0125,
        gbp: 0.01125,
        cad: 0.0175,
        aud: 0.02,
        mxn: 0.250,
        brl: 0.06875,
        jpy: 1.71875
    },
    yearly: {
        usd: 0.01,
        euro: 0.01,
        gbp: 0.00875,
        cad: 0.01375,
        aud: 0.01625,
        mxn: 0.20,
        brl: 0.055,
        jpy: 1.375
    }
};
export const getCreditPrice = ({ credits, period, currency })=>{
    const ratesForPeriod = CREDIT_PRICE_PER_CREDIT[period] || CREDIT_PRICE_PER_CREDIT.yearly;
    var _ratesForPeriod_currency;
    const rate = (_ratesForPeriod_currency = ratesForPeriod[currency]) !== null && _ratesForPeriod_currency !== void 0 ? _ratesForPeriod_currency : ratesForPeriod.usd;
    return credits * rate;
};
export const formatPriceWithCurrency = ({ amount, currency, localeId })=>{
    const sign = getCurrencySign(currency);
    const formatted = amount.toLocaleString(localeId || undefined);
    const base = CURRENCY_SIGN_RIGHT_OF_NUMBER_LOCALES.includes(localeId) ? `${formatted} ${sign}` : `${sign}${formatted}`;
    return shouldShowCurrencyText(currency) ? `${base} ${currency.toUpperCase()}` : base;
};
export const formatCreditPrice = ({ credits, period, currency, localeId })=>{
    const amount = getCreditPrice({
        credits,
        period,
        currency
    });
    return formatPriceWithCurrency({
        amount,
        currency,
        localeId
    });
};
// Tiers that show the green "Special offer" line (excludes Free and Enterprise)
export const TIERS_WITH_SPECIAL_OFFER = [
    TIERS.STANDARD,
    TIERS.PRO
];
export const CREDITS_TIER_FEATURES = {
    [TIERS.BASIC]: [
        "creditsUnlimitedFreeViewers",
        "creditsUnlimitedItems",
        "credits5GbFileStorage"
    ],
    [TIERS.STANDARD]: [
        "creditsWorkflows3",
        "creditsAutomations250",
        "creditsIntegrations250",
        "creditsTimelineAndGanttViews",
        "creditsCalendarView",
        "creditsGuestAccess",
        "creditsApiCalls1K"
    ],
    [TIERS.PRO]: [
        "creditsWorkflows20",
        "creditsAutomations25K",
        "creditsIntegrations25K",
        "creditsPrivateBoardsAndDocs",
        "creditsAdvancedBoardViews",
        "creditsTimeTracking",
        "creditsAdvancedColumns",
        "creditsApiCalls10K"
    ],
    [TIERS.ENTERPRISE]: [
        "creditsWorkflows250",
        "creditsEnterpriseSecurityAndGovernance",
        "creditsEnterpriseAutomationsAndIntegrations",
        "creditsPortfolioManagement",
        "creditsResourceManagement",
        "creditsMultiLevelPermissions",
        "creditsApiCalls25K",
        "creditsEnterpriseSupport"
    ]
};
export const CREDITS_PER_SEAT_TIER_FEATURES = {
    [TIERS.BASIC]: [
        "creditsPerSeatViewersAccess",
        "creditsPerSeatBasicBoardsViewsAndColumns"
    ],
    [TIERS.STANDARD]: [
        "creditsPerSeatAutomations250",
        "creditsPerSeatIntegrations250",
        "creditsPerSeatApiCalls2500",
        "creditsPerSeatGuestAccess"
    ],
    [TIERS.PRO]: [
        "creditsPerSeatAutomations25K",
        "creditsPerSeatIntegrations25K",
        "creditsPerSeatApiCalls10K",
        "creditsPerSeatPrivateBoardsAndDocs"
    ],
    [TIERS.ENTERPRISE]: [
        "creditsPerSeatApiCalls25K",
        "creditsPerSeatEnterpriseSecurityAndGovernance",
        "creditsPerSeatAdvancedPermissions",
        "creditsPerSeatDomainManagement",
        "creditsPerSeatAuditLog",
        "creditsPerSeatAdvancedReporting",
        "creditsPerSeatPortfolioAndResource",
        "creditsPerSeatEnterpriseSupport"
    ]
};
// Base values for monday AI features table (at included credit amounts)
// These scale proportionally when more credits are selected
export const MONDAY_AI_BASE_VALUES = {
    aiAgents: {
        [TIERS.BASIC]: 20,
        [TIERS.STANDARD]: 40,
        [TIERS.PRO]: 60,
        [TIERS.ENTERPRISE]: 400
    },
    aiMeetingNotetaker: {
        [TIERS.BASIC]: 10,
        [TIERS.STANDARD]: 20,
        [TIERS.PRO]: 30,
        [TIERS.ENTERPRISE]: 200
    },
    sidekickAiAssistant: {
        [TIERS.BASIC]: 100,
        [TIERS.STANDARD]: 200,
        [TIERS.PRO]: 300,
        [TIERS.ENTERPRISE]: 2000
    },
    aiWorkflowsBuilder: {
        [TIERS.PRO]: 375,
        [TIERS.ENTERPRISE]: 2500
    },
    aiColumns: {
        [TIERS.STANDARD]: 250,
        [TIERS.PRO]: 375,
        [TIERS.ENTERPRISE]: 2500
    }
};
export const MONDAY_AI_FEATURE_TEMPLATE_KEYS = {
    aiAgents: {
        key: "pricingPage.credits.table.aiAgents",
        enterpriseKey: "pricingPage.credits.table.aiAgents.enterprise"
    },
    aiMeetingNotetaker: {
        key: "pricingPage.credits.table.aiNotetaker",
        enterpriseKey: "pricingPage.credits.table.aiNotetaker.enterprise"
    },
    sidekickAiAssistant: {
        key: "pricingPage.credits.table.sidekick",
        enterpriseKey: "pricingPage.credits.table.sidekick.enterprise"
    },
    aiWorkflowsBuilder: {
        key: "pricingPage.credits.table.aiWorkflows",
        enterpriseKey: "pricingPage.credits.table.aiWorkflows.enterprise"
    },
    aiColumns: {
        key: "pricingPage.credits.table.aiColumns",
        enterpriseKey: "pricingPage.credits.table.aiColumns.enterprise"
    }
};
export const VIBE_APP_BUILDER_TIER_COLUMN_VALUES = {
    [TIERS.BASIC]: true,
    [TIERS.STANDARD]: true,
    [TIERS.PRO]: true,
    [TIERS.ENTERPRISE]: true
};
export const VIBE_APP_BUILDER_CREDITS_USAGE_TIER_VALUES = {
    [TIERS.BASIC]: "pricingPage.credits.usage.vibeAppBuilder.publishedApps.one",
    [TIERS.STANDARD]: "pricingPage.credits.usage.vibeAppBuilder.publishedApps.two",
    [TIERS.PRO]: "pricingPage.credits.usage.vibeAppBuilder.publishedApps.three",
    [TIERS.ENTERPRISE]: "pricingPage.credits.usage.vibeAppBuilder.publishedApps.five"
};
export const getScaledMondayAiValue = (featureKey, tier, selectedCredits)=>{
    var _MONDAY_AI_BASE_VALUES_featureKey;
    const baseValue = (_MONDAY_AI_BASE_VALUES_featureKey = MONDAY_AI_BASE_VALUES[featureKey]) === null || _MONDAY_AI_BASE_VALUES_featureKey === void 0 ? void 0 : _MONDAY_AI_BASE_VALUES_featureKey[tier];
    if (!baseValue) return false;
    const baseCredits = CREDITS_PER_TIER[tier];
    const multiplier = (selectedCredits || baseCredits) / baseCredits;
    const scaledValue = Math.floor(baseValue * multiplier);
    const templateKeys = MONDAY_AI_FEATURE_TEMPLATE_KEYS[featureKey];
    const key = tier === TIERS.ENTERPRISE ? templateKeys.enterpriseKey : templateKeys.key;
    return {
        key,
        amount: scaledValue.toLocaleString()
    };
};
export const buildCreditsMondayAiTable = (selectedCreditsPerTier = {})=>({
        sectionName: "mondayAi",
        features: [
            "aiAgents",
            "aiMeetingNotetaker",
            "sidekickAiAssistant",
            "aiWorkflowsBuilder",
            "vibeAppBuilder",
            "aiColumns"
        ].map((featureKey)=>{
            if (featureKey === "vibeAppBuilder") {
                return {
                    featureName: "vibeAppBuilder",
                    tierColumnValues: VIBE_APP_BUILDER_TIER_COLUMN_VALUES
                };
            }
            return {
                featureName: featureKey,
                tierColumnValues: {
                    [TIERS.BASIC]: getScaledMondayAiValue(featureKey, TIERS.BASIC, selectedCreditsPerTier[TIERS.BASIC]) || undefined,
                    [TIERS.STANDARD]: getScaledMondayAiValue(featureKey, TIERS.STANDARD, selectedCreditsPerTier[TIERS.STANDARD]) || undefined,
                    [TIERS.PRO]: getScaledMondayAiValue(featureKey, TIERS.PRO, selectedCreditsPerTier[TIERS.PRO]) || undefined,
                    [TIERS.ENTERPRISE]: getScaledMondayAiValue(featureKey, TIERS.ENTERPRISE, selectedCreditsPerTier[TIERS.ENTERPRISE]) || undefined
                }
            };
        })
    });
export const CREDITS_MONDAY_AI_FEATURES_TABLE = {
    sectionName: "mondayAi",
    features: [
        {
            featureName: "aiAgents",
            tierColumnValues: {
                [TIERS.BASIC]: "pricingPage.features.plans.ai_agents_basic",
                [TIERS.STANDARD]: "pricingPage.features.plans.ai_agents_standard",
                [TIERS.PRO]: "pricingPage.features.plans.ai_agents_pro",
                [TIERS.ENTERPRISE]: "pricingPage.features.plans.ai_agents_enterprise"
            }
        },
        {
            featureName: "aiMeetingNotetaker",
            tierColumnValues: {
                [TIERS.BASIC]: "pricingPage.features.plans.ai_notetaker_basic",
                [TIERS.STANDARD]: "pricingPage.features.plans.ai_notetaker_standard",
                [TIERS.PRO]: "pricingPage.features.plans.ai_notetaker_pro",
                [TIERS.ENTERPRISE]: "pricingPage.features.plans.ai_notetaker_enterprise"
            }
        },
        {
            featureName: "sidekickAiAssistant",
            tierColumnValues: {
                [TIERS.BASIC]: "pricingPage.features.plans.sidekick_basic",
                [TIERS.STANDARD]: "pricingPage.features.plans.sidekick_standard",
                [TIERS.PRO]: "pricingPage.features.plans.sidekick_pro",
                [TIERS.ENTERPRISE]: "pricingPage.features.plans.sidekick_enterprise"
            }
        },
        {
            featureName: "aiWorkflowsBuilder",
            tierColumnValues: {
                [TIERS.BASIC]: false,
                [TIERS.STANDARD]: false,
                [TIERS.PRO]: "pricingPage.features.plans.ai_workflows_pro",
                [TIERS.ENTERPRISE]: "pricingPage.features.plans.ai_workflows_enterprise"
            }
        },
        {
            featureName: "vibeAppBuilder",
            tierColumnValues: VIBE_APP_BUILDER_TIER_COLUMN_VALUES
        },
        {
            featureName: "aiColumns",
            tierColumnValues: {
                [TIERS.BASIC]: "pricingPage.features.plans.ai_columns_runs",
                [TIERS.STANDARD]: "pricingPage.features.plans.ai_columns_runs",
                [TIERS.PRO]: "pricingPage.features.plans.ai_columns_runs",
                [TIERS.ENTERPRISE]: "pricingPage.features.plans.ai_columns_runs"
            }
        }
    ]
};
